import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { stageFor, type DomainStage, type VercelChallenge } from '@/lib/customDomain';

export interface CustomDomainDnsConfig {
  ipv4?: string | null;
  cname?: string | null;
  txtFound?: boolean;
  challenges?: VercelChallenge[];
  hosts?: Array<{ name: string; attached: boolean; verified: boolean; misconfigured: boolean | null }>;
  reason?: string;
}

export interface CustomDomain {
  id: string;
  domain: string;
  include_www: boolean;
  verified_at: string | null;
  verification_token: string;
  verification_status: 'pending' | 'verified' | 'failed';
  tls_status: 'pending' | 'provisioning' | 'active' | 'error' | 'unconfigured';
  provisioning_error: string | null;
  last_checked_at: string | null;
  dns_config: CustomDomainDnsConfig | null;
  created_at: string;
}

export const DOMAIN_COLUMNS =
  'id, domain, include_www, verified_at, verification_token, verification_status, tls_status, provisioning_error, last_checked_at, dns_config, created_at';

export function domainStage(d: Pick<CustomDomain, 'verification_status' | 'tls_status'>): DomainStage {
  return stageFor(d.verification_status, d.tls_status);
}

/** Business outcome from the verify-custom-domain edge function. */
export interface DomainCheckResponse {
  ok: boolean;
  code?: 'plan_upgrade_required' | 'not_found';
  message?: string;
  stage?: DomainStage;
  domain?: CustomDomain | null;
  removed?: boolean;
  detachWarning?: string | null;
}

type RpcError = { message: string; hint?: string | null; code?: string };

// The generated Supabase types don't include these columns/RPCs yet.
type DomainDb = {
  from: (table: 'custom_domains') => {
    select: (columns: string) => {
      eq: (column: string, value: string | null) => {
        order: (column: string) => Promise<{ data: CustomDomain[] | null; error: RpcError | null }>;
      };
    };
  };
  rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: RpcError | null }>;
  functions: {
    invoke: (
      fn: 'verify-custom-domain',
      opts: { body: Record<string, unknown> },
    ) => Promise<{ data: DomainCheckResponse | null; error: { message: string } | null }>;
  };
};

const db = supabase as unknown as DomainDb;

const domainsKey = (orgId: string | null | undefined) => ['domains-admin', orgId] as const;

export function useDomainsData(orgId: string | null | undefined) {
  return useQuery({
    queryKey: domainsKey(orgId),
    enabled: Boolean(orgId),
    queryFn: async (): Promise<CustomDomain[]> => {
      const { data, error } = await db
        .from('custom_domains')
        .select(DOMAIN_COLUMNS)
        .eq('organization_id', orgId!)
        .order('created_at');
      if (error) throw error;
      return data ?? [];
    },
  });
}

/** Team / Business / trial may use a custom domain (Solo sees an upgrade prompt). */
export function useCustomDomainEntitlement(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['custom-domain-entitlement', orgId],
    enabled: Boolean(orgId),
    staleTime: 60_000,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await db.rpc('custom_domain_plan_allows', { _org_id: orgId });
      if (error) throw error;
      return data === true;
    },
  });
}

export class PlanUpgradeRequiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PlanUpgradeRequiredError';
  }
}

export function useAddDomain(orgId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (domain: string) => {
      const { error } = await db.rpc('create_domain_challenge', { _org_id: orgId!, _domain: domain });
      if (error) {
        if (error.hint === 'plan_upgrade_required') throw new PlanUpgradeRequiredError(error.message);
        if (/already connected to another organi[sz]ation/i.test(error.message)) {
          throw new Error('That domain is already connected to another business on The Polished Version.');
        }
        throw error;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: domainsKey(orgId) });
    },
  });
}

async function invokeDomainFunction(body: Record<string, unknown>): Promise<DomainCheckResponse> {
  const { data, error } = await db.functions.invoke('verify-custom-domain', { body });
  if (error) throw error;
  return data ?? { ok: false, message: 'No response from the domain checker.' };
}

function replaceDomain(list: CustomDomain[] | undefined, updated: CustomDomain): CustomDomain[] {
  return (list ?? []).map((d) => (d.id === updated.id ? { ...d, ...updated } : d));
}

/** Auto re-check cadence while the page is open. */
export function recheckIntervalFor(stage: DomainStage): number | false {
  if (stage === 'live') return false;
  if (stage === 'waiting_ssl') return 30_000;
  if (stage === 'checking_dns') return 60_000;
  return 5 * 60_000; // problem / setup_pending
}

/**
 * Runs the real DNS / Vercel / SSL check for one domain: once when the page
 * opens, then on an interval until it's live (only while the tab is visible).
 * `refetch()` is the "Check now" button. Each run writes the latest status into
 * the domains list cache.
 */
export function useDomainCheck(orgId: string | null | undefined, domain: CustomDomain | null | undefined) {
  const queryClient = useQueryClient();
  const stage = domain ? domainStage(domain) : 'checking_dns';
  return useQuery({
    queryKey: ['domain-check', domain?.id],
    enabled: Boolean(orgId && domain?.id),
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchInterval: recheckIntervalFor(stage),
    refetchIntervalInBackground: false,
    queryFn: async (): Promise<DomainCheckResponse> => {
      const res = await invokeDomainFunction({ domainId: domain!.id, action: 'check' });
      if (res.ok && res.domain) {
        queryClient.setQueryData<CustomDomain[]>(domainsKey(orgId), (list) => replaceDomain(list, res.domain!));
      } else if (res.code === 'not_found') {
        void queryClient.invalidateQueries({ queryKey: domainsKey(orgId) });
      }
      return res;
    },
  });
}

export function useRemoveDomain(orgId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (domainId: string) => invokeDomainFunction({ domainId, action: 'remove' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: domainsKey(orgId) });
    },
  });
}
